import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton } from '@mui/material';
import { format } from 'date-fns';

import ArrowLeftIcon from '~/assets/icons/svgs/arrow-left.svg?react';
import LinkIcon from '~/assets/icons/svgs/link.svg?react';
import { Text } from '~/components/ui/text';
import { LOCATION_TYPE } from '~/constants/vCard';
import useCouponTemplateStore from '~/store/useCouponTemplateStore';
import { COLORS } from '~/styles/colors';
import { mainPreviewContainerStyles } from '~/styles/common';
import { CouponQrResponse } from '~/types/coupon';
import { getDefaultAddress } from '~/utils/builder';
import { getMaxLengthDisplayedValue } from '~/utils/common';
import { copyTextToClipboard } from '~/utils/copyTextToClipboard';
import { FormatLocales } from '~/utils/formatLocales';

import { useStyles } from './CouponPreview.styled';

type CouponPreviewProps = {
  isMyMicrosite?: boolean;
  couponQrData?: CouponQrResponse;
};

export default function CouponPreview({ isMyMicrosite, couponQrData }: CouponPreviewProps) {
  const { t, i18n } = useTranslation();

  const context = useFormContext();

  const {
    watch,
    formState: { errors },
  } = context || { formState: {} };

  const { banner, barcode, locationType, showCompleteUrl } = useCouponTemplateStore();

  const [showFrontSide, setShowFrontSide] = useState(true);
  const [isCouponHasScrollHeight, setIsCouponHasScrollHeight] = useState(false);

  const termsAndConditionsContainerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const backgroundColor = isMyMicrosite ? couponQrData?.design?.primary : watch('appearance.design.primary');
  const secondaryColor = isMyMicrosite ? couponQrData?.design?.secondary : watch('appearance.design.secondary');
  const titleColor = isMyMicrosite ? couponQrData?.fontsColors?.title : watch('appearance.fonts.titleColor');
  const textsColor = isMyMicrosite ? couponQrData?.fontsColors?.text : watch('appearance.fonts.textsColor');
  const titleFontFamily = isMyMicrosite ? couponQrData?.fonts?.title : watch('appearance.fonts.title');
  const textsFontFamily = isMyMicrosite ? couponQrData?.fonts?.text : watch('appearance.fonts.text');
  const companyName = isMyMicrosite ? couponQrData?.companyName : watch('basicInfo.offer.company');
  const couponCodeText = isMyMicrosite ? couponQrData?.couponCode : watch('basicInfo.coupon.code');
  const title = isMyMicrosite ? couponQrData?.title : watch('basicInfo.offer.title');
  const salesBadge = isMyMicrosite ? couponQrData?.badgeText : watch('basicInfo.offer.salesBadge');
  const description = isMyMicrosite ? couponQrData?.description : watch('basicInfo.offer.description');
  const showCodeButtonText = isMyMicrosite ? couponQrData?.showCodeButtonText : watch('basicInfo.offer.codeButton');
  const validUntil = isMyMicrosite
    ? couponQrData?.validUntilRaw
      ? new Date(couponQrData.validUntilRaw)
      : null
    : watch('basicInfo.coupon.validUntil');
  const termsAndConditions = isMyMicrosite ? couponQrData?.termsAndConditions : watch('basicInfo.coupon.terms');
  const url = isMyMicrosite ? couponQrData?.buttonUrl : watch('basicInfo.coupon.url');
  const couponButtonText = isMyMicrosite ? couponQrData?.buttonText : watch('basicInfo.coupon.buttonText');
  const locationSearchAddress = isMyMicrosite
    ? getDefaultAddress(couponQrData?.location)
    : watch('basicInfo.coupon.location.complete.searchAddress');
  const locationLongitude = isMyMicrosite
    ? couponQrData?.location?.longitude
    : watch('basicInfo.coupon.location.coordinates.longitude');
  const locationLatitude = isMyMicrosite
    ? couponQrData?.location?.latitude
    : watch('basicInfo.coupon.location.coordinates.latitude');
  const locationFormUrl = isMyMicrosite ? couponQrData?.location?.url : watch('basicInfo.coupon.location.url');
  const locationTypeValue = isMyMicrosite ? couponQrData?.location?.type : locationType;
  const bannerData = isMyMicrosite ? couponQrData?.banner : banner;
  const barcodeData = isMyMicrosite ? couponQrData?.barcode : barcode;

  const styles = useStyles({
    primaryColor: backgroundColor,
    secondaryColor,
    textsColor,
    isCouponSide: !showFrontSide,
    textsFontFamily,
  });

  let locationUrl = '';

  if (locationTypeValue === LOCATION_TYPE.FULL) {
    if (showCompleteUrl || (isMyMicrosite && locationSearchAddress)) {
      locationUrl = `https://www.google.com/maps/search/?api=1&query=${locationSearchAddress}`;
    }
  } else if (locationTypeValue === LOCATION_TYPE.URL) {
    if (locationFormUrl && (isMyMicrosite || !errors.basicInfo?.coupon?.location?.url?.message)) {
      locationUrl = locationFormUrl;
    }
  } else if (locationTypeValue === LOCATION_TYPE.COORDINATES) {
    if (locationLatitude && locationLongitude) {
      locationUrl = `https://www.google.com/maps?q=${locationLatitude},${locationLongitude}&z=15&hl=en`;
    }
  }

  const handleScrollToTermsAndConditions = () => {
    termsAndConditionsContainerRef.current?.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
      inline: 'nearest',
    });
  };

  useEffect(() => {
    if (contentRef.current?.scrollHeight > contentRef.current?.clientHeight) {
      if (!isCouponHasScrollHeight) {
        setIsCouponHasScrollHeight(true);
      }
    } else {
      if (isCouponHasScrollHeight) {
        setIsCouponHasScrollHeight(false);
      }
    }
  }, [
    termsAndConditions,
    showFrontSide,
    companyName,
    description,
    description,
    salesBadge,
    couponButtonText,
    couponCodeText,
    title,
    url,
  ]);

  return (
    <Box
      ref={contentRef}
      sx={[
        {
          ...mainPreviewContainerStyles,
          alignItems: 'normal',
          padding: '20px 8px',
          backgroundColor,
          justifyContent: isCouponHasScrollHeight ? 'flex-start' : 'center', // justifyContent: 'center' hide top part (company name with link) in case when there is scroll in this Box
          boxSizing: 'border-box',
        },
        isMyMicrosite && { margin: 0, minHeight: '100vh', borderRadius: 0 },
      ]}
    >
      <Box sx={styles.container}>
        <Box sx={styles.frontTop}>
          <Text
            sx={{ fontSize: 11.5, fontWeight: 600 }}
            style={{ color: secondaryColor, fontFamily: titleFontFamily, wordBreak: 'break-word' }}
          >
            {companyName}
          </Text>
          <a href={url} target="_blank">
            <LinkIcon />
          </a>
        </Box>
        <Box sx={styles.topCard}>
          {showFrontSide ? (
            <Box sx={styles.frontSideContent}>
              <Box sx={styles.frontSideTop}>
                {bannerData?.image && <img src={bannerData.image} alt="banner" width="100%" height="100%" />}
                {salesBadge && (
                  <Box sx={styles.salesBadge}>
                    <Text
                      sx={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: textsColor,
                        fontFamily: textsFontFamily,
                        wordBreak: 'break-word',
                      }}
                    >
                      {getMaxLengthDisplayedValue({ value: salesBadge, maxLength: 15 })}
                    </Text>
                  </Box>
                )}
              </Box>
              <Box sx={styles.frontSideBottom}>
                <Text
                  sx={{
                    fontSize: 21,
                    color: titleColor,
                    fontWeight: 700,
                    fontFamily: titleFontFamily,
                    wordBreak: 'break-word',
                    marginTop: '16px',
                  }}
                >
                  {title}
                </Text>
                {description && (
                  <Text
                    sx={{
                      fontSize: 11.5,
                      color: COLORS.textMain,
                      fontWeight: 500,
                      fontFamily: textsFontFamily,
                      wordBreak: 'break-word',
                    }}
                  >
                    {description}
                  </Text>
                )}
              </Box>
            </Box>
          ) : (
            <>
              <IconButton onClick={() => setShowFrontSide(true)} disableRipple sx={styles.backToFront}>
                <ArrowLeftIcon />
              </IconButton>
              <Text
                sx={{
                  textAlign: 'center',
                  color: COLORS.textMain,
                  fontSize: 11.5,
                  fontWeight: 500,
                  marginTop: '24px',
                  fontFamily: titleFontFamily,
                }}
              >
                {t('builder:coupon.coupon.code')}
              </Text>
              {barcodeData?.image ? (
                <img src={barcodeData.image} alt="barcode" className="barcodeImage" />
              ) : (
                <>
                  <Text
                    sx={{
                      color: secondaryColor,
                      fontSize: 32,
                      lineHeight: '43px',
                      textAlign: 'center',
                      marginTop: '25px',
                      fontWeight: 700,
                      fontFamily: titleFontFamily,
                      wordBreak: 'break-word',
                    }}
                  >
                    {couponCodeText}
                  </Text>
                  <Button
                    variant="text"
                    disableRipple
                    onClick={() => copyTextToClipboard(couponCodeText)}
                    sx={styles.copyCode}
                  >
                    {t('builder:coupon.coupon.copyCode')}
                  </Button>
                </>
              )}
              {validUntil && (
                <Text
                  sx={{
                    fontSize: 11.5,
                    color: COLORS.grey400,
                    fontWeight: 500,
                    textAlign: 'center',
                    marginTop: '24px',
                    fontFamily: titleFontFamily,
                  }}
                >
                  {t('builder:coupon.coupon.validUntil')}{' '}
                  {format(validUntil, 'd LLL yyyy', { locale: FormatLocales[i18n.language] })}
                </Text>
              )}
              {termsAndConditions && (
                <button onClick={handleScrollToTermsAndConditions} className="termsBtn">
                  {t('builder:coupon.coupon.terms')}
                </button>
              )}
            </>
          )}
        </Box>
        {showFrontSide ? (
          <Box sx={styles.bottomCard}>
            {showCodeButtonText && (
              <Button
                sx={styles.bottomButton}
                variant="contained"
                onClick={() => setShowFrontSide(false)}
                disableRipple
              >
                {getMaxLengthDisplayedValue({ value: showCodeButtonText, maxLength: 19 })}
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={styles.bottomCard}>
            {couponButtonText && (
              <a href={url} target="_blank" className="couponButtonText">
                {getMaxLengthDisplayedValue({ value: couponButtonText, maxLength: 19 })}
              </a>
            )}
            {locationUrl && (
              <a href={locationUrl} target="_blank" className="seeLocationLink">
                {t('builder:coupon.coupon.seeLocation')}
              </a>
            )}
          </Box>
        )}
      </Box>
      <Box ref={termsAndConditionsContainerRef} sx={styles.termsAndConditionsBlock}>
        {termsAndConditions && !showFrontSide && (
          <Text
            variant="buttonSmallRegular"
            sx={{ color: textsColor, fontSize: 9.65, textAlign: 'center', fontFamily: textsFontFamily }}
          >
            {termsAndConditions}
          </Text>
        )}
      </Box>
    </Box>
  );
}
